import { useEffect, useMemo, useState } from 'react';
import { playComercio } from '../audio/mixer';
import { api } from '../api';
import { ApiError, type CidadeItem, type CidadeLoja, type FerramentaRegras, type Pack } from '../api/types';
import { t } from '../i18n/idioma';
import { nomeDoItem } from '../i18n/nomes';
import { iconeDaPrata, prefetchIcons } from '../assets/items';
import { ItemIcon } from './ItemIcon';
import { formatGold, stones } from './itemUi';

const PRATA_PESO = 0.02;
const QTD_MAX = 100;

type Categoria = 'tudo' | 'arma' | 'armadura' | 'escudo' | 'municao' | 'pocao' | 'reagente' | 'cura' | 'ferramenta' | 'roupa' | 'montaria' | 'moveis' | 'oficiosCasa' | 'outros';
const CATEGORIAS: readonly Categoria[] = ['tudo', 'arma', 'armadura', 'escudo', 'municao', 'pocao', 'reagente', 'cura', 'ferramenta', 'roupa', 'montaria', 'oficiosCasa', 'moveis', 'outros'];
/** A ordem dos conjuntos de mobília na lista (techspec-marceneiro-e-oficios-em-casa §03.2). */
const CONJUNTOS = ['aldeao', 'despensa', 'luz', 'taverna', 'biblioteca', 'quarto', 'oficios'];
const RARIDADE = ['comum', 'incomum', 'raro', 'epico', 'lendario'];
const FERRAMENTAS = new Set(['hatchet', 'pickaxe', 'golden-hatchet', 'golden-pickaxe', 'sewing-kit', 'fletcher-tools']);
const CURATIVOS = new Set(['bandage']);

/** A categoria da lista: o grupo do item, com ferramentas e curativos separados por id. */
export function categoriaDe(i: Pick<CidadeItem, 'defId' | 'grupo' | 'oficio'>): Categoria {
  if (i.oficio) return 'oficiosCasa';
  if (i.grupo === 'furniture') return 'moveis';
  const base = i.defId.split('@')[0];
  if (FERRAMENTAS.has(base)) return 'ferramenta';
  if (CURATIVOS.has(base)) return 'cura';
  switch (i.grupo) {
    case 'weapon': return 'arma';
    case 'armor': return 'armadura';
    case 'shield': return 'escudo';
    case 'ammo': return 'municao';
    case 'potion': return 'pocao';
    case 'reagent': return 'reagente';
    case 'cloth': return 'roupa';
    case 'mount': return 'montaria';
    default: return 'outros';
  }
}

const chave = (i: Pick<CidadeItem, 'npcId' | 'defId'>) => `${i.npcId}|${i.defId}`;
const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

interface Props {
  active: boolean;
  tick: number;
  /** Aberta pelo balcão de um NPC: já filtrada na loja dele. */
  vendedorInicial?: string | null;
  onResultado(ev: { evento: 'compra' | 'semPrata' | 'semPeso'; total?: number }): void;
}

/**
 * As compras da cidade (Renato, 29/10/2026): tudo o que as lojas de NPC da sala vendem, numa janela só, com busca,
 * categorias, filtro por vendedor, "só o que posso usar" e um carrinho que junta lojas diferentes numa compra. Os NPCs
 * continuam definindo o que vendem e o preço; o balcão de cada um abre esta mesma janela, filtrada nele.
 */
export function CompraCidadeGump({ active, tick, vendedorInicial = null, onResultado }: Props) {
  const [loja, setLoja] = useState<CidadeLoja | null>(null);
  const [pack, setPack] = useState<Pack | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [cat, setCat] = useState<Categoria>('tudo');
  const [vend, setVend] = useState<string | null>(vendedorInicial);
  const [busca, setBusca] = useState('');
  const [soUsa, setSoUsa] = useState(false);
  const [ordem, setOrdem] = useState<'cat' | 'barato' | 'nome'>('cat');
  const [carrinho, setCarrinho] = useState<Record<string, number>>({});
  const [aviso, setAviso] = useState<string | null>(null);
  const [rar, setRar] = useState<'todas' | 0 | 1>('todas');
  const [regras, setRegras] = useState<readonly FerramentaRegras[]>([]);

  useEffect(() => { setVend(vendedorInicial); setCat('tudo'); }, [vendedorInicial]);

  useEffect(() => {
    if (!active) return;
    let vivo = true;
    void api.cidadeLoja()
      .then(next => {
        if (!vivo) return;
        setLoja(next);
        setErro(null);
        prefetchIcons(next.itens.map(i => i.defId));
      })
      .catch(err => { if (vivo) setErro(err instanceof ApiError ? err.message : t('loja.naoComprou')); });
    // Os números das ferramentas de ofício (bônus da casa), para o card da lendária.
    void api.oficiosRegras()
      .then(r => { if (vivo) setRegras(r.ferramentas); })
      .catch(() => undefined);
    void api.getPack()
      .then(next => { if (vivo) setPack(next); })
      .catch(() => undefined);
    return () => { vivo = false; };
  }, [active, tick]);

  const itens = useMemo(() => loja?.itens ?? [], [loja]);
  const forca = loja?.forca ?? 0;
  const naoUsa = (i: CidadeItem) => i.forca > forca;
  const vendedores = useMemo(() => {
    const vistos = new Map<string, { id: string; nome: string; titulo: string }>();
    for (const i of itens) if (!vistos.has(i.npcId)) vistos.set(i.npcId, { id: i.npcId, nome: i.npcNome, titulo: i.npcTitulo });
    return [...vistos.values()];
  }, [itens]);

  const q = semAcento(busca.trim());
  const daLoja = itens.filter(i => !vend || i.npcId === vend);
  // O filtro de raridade: só com o Marceneiro escolhido ou na categoria Móveis.
  const comRaridade = cat === 'moveis' || (!!vend && vendedores.find(v => v.id === vend)?.titulo === 'npc.titulo.marceneiro');
  const contagem = (c: Categoria) => daLoja.filter(i => c === 'tudo' || categoriaDe(i) === c).length;
  let lista = daLoja.filter(i => (cat === 'tudo' || categoriaDe(i) === cat)
    && (!soUsa || !naoUsa(i))
    && (!comRaridade || rar === 'todas' || i.oficio || (i.raridade ?? 0) === rar)
    && (!q || semAcento(nomeDoItem(i.defId, i.nome)).includes(q)));
  if (ordem === 'barato') lista = [...lista].sort((a, b) => a.preco - b.preco);
  if (ordem === 'nome') lista = [...lista].sort((a, b) => nomeDoItem(a.defId, a.nome).localeCompare(nomeDoItem(b.defId, b.nome)));
  if (ordem === 'cat') lista = [...lista].sort((a, b) => CATEGORIAS.indexOf(categoriaDe(a)) - CATEGORIAS.indexOf(categoriaDe(b))
    || (a.grupo === 'furniture' && b.grupo === 'furniture'
      ? CONJUNTOS.indexOf(a.conjunto ?? '') - CONJUNTOS.indexOf(b.conjunto ?? '') || a.preco - b.preco : 0));

  // As contas do carrinho: a prata sai da mochila primeiro (e alivia o peso), o resto do banco — como no balcão.
  const linhas = itens.map(i => ({ i, q: carrinho[chave(i)] ?? 0 })).filter(l => l.q > 0);
  const n = linhas.reduce((s, l) => s + l.q, 0);
  const total = linhas.reduce((s, l) => s + l.q * l.i.preco, 0);
  const mochila = pack?.silver ?? 0;
  const banco = pack?.prataBanco ?? 0;
  const daMochila = Math.min(mochila, total);
  const doBanco = Math.max(0, total - mochila);
  const pesoAtual = pack?.weight ?? 0;
  const pesoMax = pack?.weightMax ?? 0;
  const pesoFinal = pesoAtual + linhas.reduce((s, l) => s + l.q * l.i.peso, 0) - daMochila * PRATA_PESO;
  const semPrata = total > mochila + banco;
  const semPeso = pack != null && pesoFinal > pesoMax + 1e-9;
  const motivo = !n ? '' : semPrata
    ? t('compra.faltaPrata', { n: formatGold(total - mochila - banco) })
    : semPeso ? t('compra.passaPeso', { a: stones(pesoFinal), b: stones(pesoMax) }) : '';

  const mudar = (i: CidadeItem, qtd: number) => {
    // A ferramenta de ofício é uma por personagem (§03.1); quem já tem não leva outra.
    const teto = i.oficio ? (i.jaTem ? 0 : 1) : i.empilha ? QTD_MAX : Math.min(QTD_MAX, i.preco >= 1000 ? 1 : QTD_MAX);
    const v = Math.max(0, Math.min(teto, Math.trunc(qtd) || 0));
    setAviso(null);
    setCarrinho(cur => {
      const next = { ...cur };
      if (v > 0) next[chave(i)] = v;
      else delete next[chave(i)];
      return next;
    });
  };

  const comprar = async () => {
    if (!n || semPrata || semPeso) return;
    setOcupado(true);
    setErro(null);
    try {
      const novo = await api.comprarCidade(linhas.map(l => ({ npcId: l.i.npcId, defId: l.i.defId, qty: l.q })));
      playComercio();
      setPack(novo);
      setCarrinho({});
      const temMovel = linhas.some(l => l.i.grupo === 'furniture');
      setAviso(t('compra.feita', { n, total: formatGold(total) }) + (temMovel ? ` ${t('compra.dicaCasa')}` : ''));
      if (linhas.some(l => l.i.oficio)) void api.cidadeLoja().then(setLoja).catch(() => undefined);
      onResultado({ evento: 'compra', total });
    } catch (err) {
      if (err instanceof ApiError) {
        setErro(err.message);
        if (err.code === 'loja.semPrata') onResultado({ evento: 'semPrata' });
        else if (err.code === 'loja.semPeso') onResultado({ evento: 'semPeso' });
      } else {
        setErro(t('loja.naoComprou'));
      }
    } finally {
      setOcupado(false);
    }
  };

  const vendNome = (id: string) => vendedores.find(v => v.id === id);
  const qtdCampo = (i: CidadeItem, qtd: number) => (
    <span className="cc-qtd">
      <button type="button" aria-label={t('compra.menos')} disabled={ocupado} onClick={() => mudar(i, qtd - 1)}>−</button>
      <input
        type="text"
        inputMode="numeric"
        value={qtd}
        disabled={ocupado}
        aria-label={t('mercado.quantidadeDe', { item: nomeDoItem(i.defId, i.nome) })}
        onChange={e => mudar(i, Number(e.target.value.replace(/\D/g, '') || '0'))}
      />
      <button type="button" aria-label={t('compra.mais')} disabled={ocupado} onClick={() => mudar(i, qtd + 1)}>+</button>
    </span>
  );

  return (
    <div className="cc">
      <div className="cc-barra">
        <label className="cc-busca">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg>
          <input value={busca} placeholder={t('compra.buscar')} aria-label={t('compra.buscar')} onChange={e => setBusca(e.target.value)} />
        </label>
        <div className="cc-vchips" role="group" aria-label={t('compra.vendedor')}>
          <button type="button" className="cc-vchip" aria-pressed={!vend} onClick={() => setVend(null)}>{t('compra.todas')}</button>
          {vendedores.map(v => (
            <button key={v.id} type="button" className="cc-vchip" aria-pressed={vend === v.id} title={v.nome} onClick={() => { setVend(v.id); setCat('tudo'); }}>
              {t(v.titulo)}
            </button>
          ))}
        </div>
        {comRaridade && (
          <div className="cc-vchips cc-rar" role="group" aria-label={t('compra.raridade')}>
            {(['todas', 0, 1] as const).map(r => (
              <button key={r} type="button" className="cc-vchip" aria-pressed={rar === r} onClick={() => setRar(r)}>
                {r === 'todas' ? t('compra.rarTodas') : t(`raridade.${RARIDADE[r]}`)}
              </button>
            ))}
          </div>
        )}
        <div className="cc-opc">
          <label><input type="checkbox" checked={soUsa} onChange={e => setSoUsa(e.target.checked)} /> {t('compra.soUsa')}</label>
          <select value={ordem} aria-label={t('compra.ordem')} onChange={e => setOrdem(e.target.value as 'cat' | 'barato' | 'nome')}>
            <option value="cat">{t('compra.ordem.cat')}</option>
            <option value="barato">{t('compra.ordem.barato')}</option>
            <option value="nome">{t('compra.ordem.nome')}</option>
          </select>
        </div>
      </div>
      {erro && <p className="notice" role="alert">{erro}</p>}
      <div className="cc-corpo">
        <nav className="cc-cats" aria-label={t('compra.categorias')}>
          {CATEGORIAS.filter(c => contagem(c) > 0).map(c => (
            <button key={c} type="button" className="cc-cat" aria-pressed={cat === c} onClick={() => setCat(c)}>
              {t(`compra.cat.${c}`)}<small>{contagem(c)}</small>
            </button>
          ))}
        </nav>
        <div className="cc-lista" aria-label={t('compra.itens')}>
          {!loja && !erro && <p className="cc-vazio">{t('comum.montando')}</p>}
          {loja && lista.length === 0 && <p className="cc-vazio">{itens.length === 0 ? t('compra.semLojas') : t('compra.nadaFiltro')}</p>}
          {lista.map((i, k) => {
            const qtd = carrinho[chave(i)] ?? 0;
            const v = vendNome(i.npcId);
            const nome = nomeDoItem(i.defId, i.nome);
            const ant = lista[k - 1];
            const grupoConjunto = ordem === 'cat' && i.grupo === 'furniture' && i.conjunto && (!ant || ant.conjunto !== i.conjunto)
              ? <p key={`g-${i.conjunto}-${k}`} className="cc-grupo">{t(`mobilia.conjunto.${i.conjunto}`)}</p> : null;
            if (i.oficio) return [grupoConjunto, <Lendaria key={chave(i)} i={i} regra={regras.find(r => r.oficio === i.oficio) ?? null}
              noCarrinho={qtd > 0} ocupado={ocupado} vendedor={v ? `${t(v.titulo)} · ${v.nome}` : ''} onPor={() => mudar(i, qtd ? 0 : 1)} />];
            return [grupoConjunto, (
              <div key={chave(i)} className={`cc-it${qtd ? ' is-carrinho' : ''}${naoUsa(i) ? ' is-naousa' : ''}`}>
                <span className="cc-it__ic"><ItemIcon id={i.defId} label={nome} size={36} eager /></span>
                <span className="cc-it__meio">
                  <span className="cc-it__n">{nome}</span>
                  <span className="cc-it__s">
                    {i.grupo === 'furniture' && <span className={`cc-rarchip is-${RARIDADE[i.raridade ?? 0]}`}>{t(`raridade.${RARIDADE[i.raridade ?? 0]}`)}</span>}
                    {i.luz && <span className="cc-tag is-luz">{t('compra.luz')}</span>}
                    <Stats i={i} naoUsa={naoUsa(i)} />
                  </span>
                  {v && (
                    <button type="button" className="cc-quem" title={t('compra.soEste', { nome: v.nome })} onClick={() => { setVend(v.id); setCat('tudo'); }}>
                      {t(v.titulo)} · {v.nome}
                    </button>
                  )}
                </span>
                <span className="cc-it__fim">
                  <span className="cc-preco" aria-label={t('loja.preco', { n: i.preco })}>
                    <img src={iconeDaPrata()} alt="" width={16} height={16} draggable={false} />{formatGold(i.preco)}
                  </span>
                  {qtd ? qtdCampo(i, qtd) : (
                    <button type="button" className="cc-add" disabled={ocupado} aria-label={t('compra.por', { item: nome })} onClick={() => mudar(i, 1)}>+</button>
                  )}
                </span>
              </div>
            )];
          })}
        </div>
        <aside className="cc-carro" aria-label={t('compra.carrinho')}>
          <div className="cc-carro__t">
            <span>{t('compra.carrinho')}{n ? ` (${n})` : ''}</span>
            {n > 0 && <button type="button" onClick={() => setCarrinho({})}>{t('compra.limpar')}</button>}
          </div>
          <div className="cc-carro__lista">
            {linhas.length === 0
              ? <p className="cc-carro__vazio">{aviso ?? t('compra.carrinhoVazio')}</p>
              : linhas.map(({ i, q: qtd }) => (
                <div key={chave(i)} className="cc-cl">
                  <ItemIcon id={i.defId} label="" size={28} eager />
                  <span className="cc-cl__n">{nomeDoItem(i.defId, i.nome)}<small>{t(i.npcTitulo)} · {i.npcNome}</small></span>
                  <span className="cc-cl__d">{qtdCampo(i, qtd)}<b>{formatGold(i.preco * qtd)}</b></span>
                </div>
              ))}
          </div>
          <div className="cc-carro__pe">
            <div className="cc-v cc-v--total"><span>{t('loja.total')}</span><b>{formatGold(total)}</b></div>
            {total > 0 && !semPrata && (
              <>
                <div className="cc-v"><span>{t('compra.daMochila')}</span><b>{formatGold(daMochila)}</b></div>
                {doBanco > 0 && <div className="cc-v"><span>{t('compra.doBanco')}</span><b>{formatGold(doBanco)}</b></div>}
              </>
            )}
            <div className="cc-v cc-v--saldo"><span>{t('compra.saldo')}</span><b><img src={iconeDaPrata()} alt="" width={14} height={14} draggable={false} />{formatGold(mochila)} + {formatGold(banco)}</b></div>
            {pack && (
              <div className="cc-peso">
                <div className="cc-v"><span>{t('compra.peso')}</span><b>{stones(pesoFinal)} / {stones(pesoMax)}</b></div>
                <div className={`cc-peso__t${semPeso ? ' is-cheio' : ''}`}>
                  <i style={{ width: `${Math.min(100, pesoAtual / Math.max(1, pesoMax) * 100)}%` }} />
                  <s style={{ left: `${Math.min(100, pesoAtual / Math.max(1, pesoMax) * 100)}%`, width: `${Math.max(0, Math.min(100, (pesoFinal - pesoAtual) / Math.max(1, pesoMax) * 100))}%` }} />
                </div>
              </div>
            )}
            {motivo && <p className="cc-motivo" role="alert">{motivo}</p>}
            <button type="button" className="btn btn--primary cc-comprar" disabled={ocupado || !n || semPrata || semPeso} onClick={() => void comprar()}>
              {n ? t('compra.comprarN', { n }) : t('loja.comprar')}
            </button>
            <p className="cc-nota">{t('compra.nota')}</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

const SLOTS: Record<string, string> = { helm: 'compra.slot.cabeca', chest: 'compra.slot.peito', arms: 'compra.slot.bracos', gloves: 'compra.slot.maos', legs: 'compra.slot.pernas', gorget: 'compra.slot.pescoco', shield: 'compra.slot.esquerda' };

function Stats({ i, naoUsa }: { i: CidadeItem; naoUsa: boolean }) {
  const partes: React.ReactNode[] = [];
  if (i.dano) partes.push(<span key="d">{t('compra.s.dano')} <b>{i.dano}</b></span>);
  if (i.ar > 0) partes.push(<span key="ar">AR <b>{i.ar}</b></span>);
  if (SLOTS[i.slot]) partes.push(<span key="sl">{t(SLOTS[i.slot])}</span>);
  if (i.maos === 1 || i.maos === 2) partes.push(<span key="m">{t(i.maos === 2 ? 'compra.s.duasMaos' : 'compra.s.umaMao')}</span>);
  if (i.alcance && i.alcance > 1) partes.push(<span key="al">{t('compra.s.alcance', { n: i.alcance })}</span>);
  if (i.forca > 0) partes.push(<span key="f" className={naoUsa ? 'is-ruim' : undefined}>{t('compra.s.forca', { n: i.forca })}</span>);
  if (i.peso > 0) partes.push(<span key="p">{stones(i.peso)} {t('comum.stones')}</span>);
  return <>{partes}</>;
}

/**
 * A ferramenta de ofício lendária no balcão (techspec-marceneiro-e-oficios-em-casa §03.2): o card largo com o bônus que ela
 * dá fixa na casa, os números do console, e uma por personagem ("Você já tem").
 */
function Lendaria({ i, regra, noCarrinho, ocupado, vendedor, onPor }: {
  i: CidadeItem; regra: FerramentaRegras | null; noCarrinho: boolean; ocupado: boolean; vendedor: string; onPor(): void;
}) {
  const nome = nomeDoItem(i.defId, i.nome);
  const bonus: string[] = [];
  if (regra) {
    if (regra.ganho) bonus.push(t('compra.lend.ganho', { n: regra.ganho }));
    if (regra.atributo) bonus.push(t('compra.lend.atributo', { n: regra.atributo }));
    if (regra.tempo) bonus.push(t('compra.lend.tempo', { n: regra.tempo }));
    if (regra.excepcional) bonus.push(t('compra.lend.excepcional', { n: regra.excepcional }));
    if (regra.economia) bonus.push(t('compra.lend.economia', { n: regra.economia }));
  }
  return (
    <div className={`cc-lend${noCarrinho ? ' is-carrinho' : ''}`}>
      <span className="cc-lend__arte"><ItemIcon id={i.defId} label={nome} size={72} eager /></span>
      <span className="cc-lend__meio">
        <span className="cc-it__n">{nome}</span>
        <span className="cc-it__s">
          <span className="cc-rarchip is-lendario">{t('raridade.lendario')}</span>
          <span>{t(`compra.oficio.${i.oficio}`)}</span>
          <span>{t('compra.lend.vaga')}</span>
        </span>
        {bonus.length > 0 && <ul className="cc-lend__b">{bonus.map(b => <li key={b}>{b}</li>)}</ul>}
        <span className="cc-lend__r">{t('compra.lend.regra')}</span>
        {vendedor && <span className="cc-quem cc-quem--fixo">{vendedor}</span>}
      </span>
      <span className="cc-it__fim">
        <span className="cc-preco" aria-label={t('loja.preco', { n: i.preco })}>
          <img src={iconeDaPrata()} alt="" width={16} height={16} draggable={false} />{formatGold(i.preco)}
        </span>
        {i.jaTem
          ? <span className="cc-jatem">{t('compra.jaTem')}</span>
          : <button type="button" className={`btn btn--tiny${noCarrinho ? '' : ' btn--primary'}`} disabled={ocupado} onClick={onPor}>
            {noCarrinho ? t('compra.tirar') : t('loja.comprar')}
          </button>}
      </span>
    </div>
  );
}
